import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { clearUserCache } from '../services/api';

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    getIdTokenClaims
  } = useAuth0();

  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        if (isLoading) {
          return;
        }

        if (isAuthenticated && auth0User) {
          try {
            // Get tokens
            const accessToken = await getAccessTokenSilently();
            
            // Create user object compatible with existing code
            const userObj = {
              id: auth0User.sub,
              email: auth0User.email,
              user_metadata: {
                full_name: auth0User.name,
                avatar_url: auth0User.picture
              },
              email_verified: auth0User.email_verified
            };

            // Create session object
            const sessionObj = {
              access_token: accessToken,
              user: userObj
            };

            if (mounted) {
              setUser(userObj);
              setSession(sessionObj);
            }

            // Note: User synchronization is now handled automatically by backend middleware
            // No need to manually sync here anymore
          } catch (tokenError) {
            console.error('Error getting tokens:', tokenError);
          }
        } else {
          if (mounted) {
            setUser(null);
            setSession(null);
          }
        }

        if (mounted) {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();
    
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, auth0User, isLoading, getAccessTokenSilently, getIdTokenClaims]);

  const signUp = async (email, password, fullName) => {
    // Temporarily disabled - return error message
    return { 
      data: null, 
      error: { 
        message: 'Email/password registration is temporarily disabled. Please use Google to create your account.' 
      } 
    };
    
    /* TEMPORARILY DISABLED
    try {
      await loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
          login_hint: email
        }
      });
      
      return { data: { user: null }, error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { data: null, error: error };
    }
    */
  };

  const signIn = async (email, password) => {
    // Temporarily disabled - return error message
    return { 
      data: null, 
      error: { 
        message: 'Email/password sign-in is temporarily disabled. Please use Google to access your account.' 
      } 
    };
    
    /* TEMPORARILY DISABLED
    try {
      await loginWithRedirect({
        authorizationParams: {
          login_hint: email
        }
      });
      
      return { data: { user: null }, error: null };
    } catch (error) {
      console.error('Signin error:', error);
      return { data: null, error: error };
    }
    */
  };

  const signInWithGoogle = async () => {
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: 'google-oauth2'
        }
      });
      
      return { data: { user: null }, error: null };
    } catch (error) {
      console.error('Google signin error:', error);
      return { data: null, error: error };
    }
  };

  const signOut = async () => {
    // Clear user profile cache on logout
    clearUserCache();
    
    try {
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
      
      return { error: null };
    } catch (error) {
      console.error('Logout error:', error);
      return { error: error };
    }
  };

  const updateProfile = async (updates) => {
    try {
      // Auth0 profile updates would typically be done through the Management API
      // For now, we'll update the local user state
      // Backend middleware will sync changes automatically on next request
      const updatedUser = {
        ...user,
        user_metadata: {
          ...user.user_metadata,
          ...updates
        }
      };
      
      setUser(updatedUser);
      
      return { data: { user: updatedUser }, error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const value = {
    user,
    session,
    loading: loading || isLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    // Add Auth0 specific methods for direct access if needed
    auth0: {
      isAuthenticated,
      loginWithRedirect,
      getAccessTokenSilently,
      getIdTokenClaims
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 